<template>
  <div class="max-w-3xl mx-auto space-y-6">
    <!-- Header -->
    <div class="text-center space-y-2">
      <h2 class="text-2xl font-bold text-slate-900 dark:text-white">
        {{ $t('AI_ONBOARDING.PROVIDER.TITLE') || 'Kết nối AI Provider' }}
      </h2>
      <p class="text-sm text-slate-600 dark:text-slate-400">
        {{ $t('AI_ONBOARDING.PROVIDER.SUBTITLE') || 'Cấu hình OpenAI để sử dụng AI chatbot' }}
      </p>
    </div>

    <!-- Configuration Form -->
    <div class="space-y-4 p-5 bg-white/50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
      <!-- Credential Mode -->
      <div>
        <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {{ $t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.MODE_LABEL') || 'Chế độ' }}
        </label>
        <select
          v-model="formData.credentialMode"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 focus:ring-2 focus:ring-hichat-500 text-sm"
          @change="handleModeChange"
        >
          <option value="system">{{ $t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.MODE_OPTIONS.SYSTEM') || 'Sử dụng cấu hình hệ thống' }}</option>
          <option value="customer">{{ $t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.MODE_OPTIONS.CUSTOM') || 'Tự cấu hình' }}</option>
        </select>
        <p v-if="isSystemMode" class="text-xs text-slate-600 dark:text-slate-400 mt-2">
          {{ $t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.SYSTEM_HINT') || 'HiCHAT bot sẽ sử dụng cấu hình AI được quản lý cho gói của bạn.' }}
        </p>
      </div>

      <!-- System Mode Message -->
      <div v-if="isSystemMode" class="rounded-lg border border-dashed border-hilab/30 dark:border-hilab/30 bg-hilab/5 dark:bg-hilab/10 px-4 py-3 text-sm text-heading">
        {{ $t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.SYSTEM_MESSAGE') || 'HiCHAT bot sẽ sử dụng cấu hình AI được quản lý cho gói của bạn.' }}
      </div>

      <!-- Customer Mode Configuration -->
      <template v-if="!isSystemMode">
        <!-- Help Card -->
        <div class="flex gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm">
          <lucide-icon name="info" :size="16" class="text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
          <div class="flex-1 text-blue-900 dark:text-blue-100">
            <strong>{{ $t('AI_ONBOARDING.PROVIDER.HELP_TITLE') || 'Làm sao để lấy API Key?' }}</strong>
            <p class="text-xs mt-1">{{ $t('AI_ONBOARDING.PROVIDER.OPENAI_HELP') || 'Truy cập platform.openai.com/api-keys, đăng nhập và tạo API key mới' }}</p>
            <a href="https://platform.openai.com/docs/quickstart" target="_blank" rel="noopener" class="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline mt-1">
              {{ $t('GENERAL.VIEW_GUIDE') || 'Xem hướng dẫn' }}
              <lucide-icon name="external-link" :size="12" />
            </a>
          </div>
        </div>

        <!-- Model Selection -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {{ $t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.MODEL') || 'Model' }} <span class="text-red-500">*</span>
          </label>
          <select
            v-model="formData.model"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 focus:ring-2 focus:ring-hichat-500 text-sm"
            @change="clearTestResult"
          >
            <option v-for="m in openaiModels" :key="m.value" :value="m.value">
              {{ m.label }}{{ m.popular ? ' - ' + ($t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.MODEL_POPULAR') || 'Phổ biến') : '' }}
            </option>
          </select>
          <p v-if="selectedModelNote" class="text-xs text-slate-600 dark:text-slate-400 mt-1">
            {{ selectedModelNote }}
          </p>
        </div>

        <!-- API Key -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            API Key <span class="text-red-500">*</span>
          </label>
          <div class="flex gap-2">
            <div class="relative flex-1">
              <input
                v-model="formData.apiKey"
                type="text"
                :style="formData.apiKey && !showApiKey ? { WebkitTextSecurity: 'disc' } : null"
                class="w-full px-3 py-2 !pr-8 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-sm"
                placeholder="sk-..."
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                spellcheck="false"
                @input="clearTestResult"
              />
              <button
                v-if="formData.apiKey"
                type="button"
                class="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 z-10"
                @click="showApiKey = !showApiKey"
              >
                <lucide-icon :name="showApiKey ? 'eye-closed' : 'eye'" :size="16" />
              </button>
            </div>
            <woot-button
              variant="smooth"
              color-scheme="secondary"
              :disabled="!canTest || isTesting"
              @click="testConnection"
            >
              {{ testButtonText }}
            </woot-button>
          </div>
          <p v-if="errors.apiKey" class="text-xs text-red-600 dark:text-red-400 mt-1">{{ errors.apiKey }}</p>
          
          <div v-if="testResult" class="flex items-center gap-2 px-4 py-2 rounded-lg text-sm mt-2" :class="testResult.status === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'">
            <lucide-icon :name="testResult.status === 'success' ? 'circle-check-big' : 'circle-alert'" :size="16" />
            {{ testResult.message }}
          </div>
        </div>
      </template>
    </div>

  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import LucideIcon from 'dashboard/components/LucideIcon.vue';
import AiOnboardingAPI from 'dashboard/api/aiOnboarding';

export default {
  name: 'AiProviderSetupStep',
  
  components: {
    LucideIcon,
  },

  emits: ['next', 'back'],

  data() {
    return {
      formData: {
        credentialMode: 'customer',
        apiKey: '',
        model: '',
        apiUrl: 'https://api.openai.com/v1/chat/completions',
      },
      errors: {},
      showApiKey: false,
      isTesting: false,
      testResult: null,
      openaiModels: [],
      defaultModel: 'gpt-4o-mini',
    };
  },

  computed: {
    ...mapState('aiOnboarding', { providerState: state => state.providerSetup }),
    
    isSystemMode() {
      return this.formData.credentialMode === 'system';
    },

    selectedModelNote() {
      const selected = this.openaiModels.find(m => m.value === this.formData.model);
      return selected?.note || '';
    },

    canTest() {
      return !this.isSystemMode && this.formData.apiKey && this.formData.model;
    },

    testButtonText() {
      if (this.isTesting) return this.$t('AI_ONBOARDING.PROVIDER.TESTING') || 'Đang kiểm tra...';
      if (this.testResult?.status === 'success') return this.$t('AI_ONBOARDING.PROVIDER.CONNECTED') || 'Đã kết nối';
      return this.$t('AI_ONBOARDING.PROVIDER.TEST_BTN') || 'Test kết nối';
    },

    isValid() {
      if (this.isSystemMode) return true;
      return this.testResult?.status === 'success';
    },

    // Expose canProceed để container có thể check
    canProceed() {
      if (this.isSystemMode) return true;
      // Customer mode: cần có apiKey và model (không cần test connection để enable nút)
      return !!(this.formData.apiKey && this.formData.model);
    },
  },

  watch: {
    'formData.credentialMode'(newVal) {
      this.updateProvider({
        credentialMode: newVal,
        provider: 'openai',
        apiKey: newVal === 'system' ? '' : this.formData.apiKey,
        model: newVal === 'system' ? '' : (this.formData.model || this.defaultModel),
        apiUrl: this.formData.apiUrl,
      });
    },
    
    'formData.apiKey'(newVal) {
      if (!this.isSystemMode && newVal && this.formData.model) {
        this.updateProvider({
          credentialMode: this.formData.credentialMode,
          provider: 'openai',
          apiKey: newVal,
          model: this.formData.model,
          apiUrl: this.formData.apiUrl,
        });
      }
    },
    
    'formData.model'(newVal) {
      if (!this.isSystemMode && newVal && this.formData.apiKey) {
        this.updateProvider({
          credentialMode: this.formData.credentialMode,
          provider: 'openai',
          apiKey: this.formData.apiKey,
          model: newVal,
          apiUrl: this.formData.apiUrl,
        });
      }
    },
  },

  async mounted() {
    await this.loadOpenaiModels();
    
    if (this.providerState.credentialMode) {
      this.formData = { 
        ...this.formData, 
        ...this.providerState,
        apiKey: this.providerState.apiKey || '',
        model: this.providerState.model || this.defaultModel,
      };
    } else {
      this.formData.model = this.defaultModel;
    }
  },

  methods: {
    ...mapActions('aiOnboarding', ['updateProvider']),

    // Expose method để container có thể gọi
    getCanProceed() {
      return this.canProceed;
    },

    async loadOpenaiModels() {
      try {
        const accountId = this.$route.params.accountId;
        const response = await window.axios.get(`/api/v1/accounts/${accountId}/ai_configurations/openai_models`);
        this.openaiModels = response.data.models || [];
        this.defaultModel = response.data.default || 'gpt-4o-mini';
      } catch (error) {
        console.error('Failed to load OpenAI models:', error);
        this.openaiModels = [
          { value: 'gpt-4o-mini', label: 'GPT-4o Mini', popular: true, recommended: true },
          { value: 'gpt-4o', label: 'GPT-4o' },
        ];
      }
    },

    handleModeChange() {
      this.testResult = null;
      this.errors = {};
    },

    clearTestResult() {
      this.testResult = null;
    },

    async testConnection() {
      this.isTesting = true;
      this.testResult = null;

      try {
        const response = await AiOnboardingAPI.testConnection({
          provider: 'openai',
          api_key: this.formData.apiKey,
          model: this.formData.model,
          api_url: this.formData.apiUrl,
        });

        this.testResult = {
          status: response.data.success ? 'success' : 'error',
          message: response.data.success 
            ? (this.$t('AI_ONBOARDING.PROVIDER.SUCCESS') || '✅ Kết nối thành công!')
            : (response.data.message || this.$t('AI_ONBOARDING.PROVIDER.FAILED') || 'Không thể kết nối'),
        };
      } catch (error) {
        this.testResult = {
          status: 'error',
          message: error.response?.data?.message || (this.$t('AI_ONBOARDING.PROVIDER.ERROR') || 'Lỗi kết nối. Kiểm tra API key và thử lại.'),
        };
      } finally {
        this.isTesting = false;
      }
    },

    async handleNext() {
      if (!this.isValid) {
        this.errors.apiKey = this.$t('AI_ONBOARDING.PROVIDER.TEST_REQUIRED') || 'Vui lòng test kết nối thành công';
        return;
      }

      await this.updateProvider({
        credentialMode: this.formData.credentialMode,
        provider: 'openai',
        apiKey: this.isSystemMode ? '' : this.formData.apiKey,
        model: this.isSystemMode ? '' : this.formData.model,
        apiUrl: this.formData.apiUrl,
      });

      this.$emit('next');
    },
  },
};
</script>
